/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'@waca/dashboard-livewidget/js/widgets/livewidget/nls/StringResources',
	'react',
	'prop-types',
	'@ba/ba-ui-carbon-toolkit',
	'underscore',
	'@ba/ba-ui-carbon'
], function(stringResources, React, PropTypes, Toolkit, _, Carbon) {
	// TODO: once ca-ui-carbon-toolkit tooltip bug and the text-input state bug is fixed bring in diabled state for the text-input
	const { Label, Tooltip, FlexLayout, VSpacer } = Toolkit;
	const { TextInput, Dropdown } = Carbon;
	class ForecastPredictComponents extends React.Component {
		static propTypes = {
			isDisabled: PropTypes.bool,
			settings: PropTypes.array,
			onForecastPredictItemsChanged: PropTypes.func
		};

		constructor(props) {
			super(props);
			let clearedSettings = this._clearValidationErrorForAllSettings(_.clone(props.settings));
			this.state = {
				settings: clearedSettings
			};
		}

		/**
		* Validation method for Number input
		* @param {String} value - value entered by the user
		* @param {String} id - id of the item being changed.
		* return {Boolean} true iff value is a number and within a specified range.
		*/
		validateNumberInput(value, id) {
			if (_.isEmpty(value)) { return true; }
			const valueAsNum = Number(value);
			if (!_.isNumber(valueAsNum)) {
				return false;
			}
			const item = _.find(this.state.settings, item => {return item.id === id;});
			const min = item.restrictions.minimum;
			const max = item.restrictions.maximum;
			return (min <= valueAsNum  && valueAsNum <= max);
		}

		_clearValidationErrorForAllSettings(settings) {
			// If we are blurring then we know the value is valid, ensure the state is seen that way.
			let newSettings = _.clone(settings);
			_.each(newSettings, (item) => {
				item.showValidationError = false;
			});
			return newSettings;
		}

		/**
		* Ensure the value is valid input. If not, set the error state.
		* @param {Object} item of interest
		* @param {String} value entered by a user, checked to be a valid number or null
		*/
		_validateNumberInputForItem(item, value) {
			const isValid = this.validateNumberInput(value, item.id);
			if (!isValid) {
				item.showValidationError = true;
				this.setState({ validationError: true });
			}
			return isValid;
		}

		/**
		* If the value is valid for the item, clear the state error, set the state and the model.
		* @param {Object} item of interest
		* @param {String} value entered by a user, checked to be a valid number or null
		*/
		_checkAndSetNumberValues(item, value) {
			if(this._validateNumberInputForItem(item, value)) {
				// If we are blurring then we know the value is valid, ensure the state is seen that way.
				let clearedSettings = this._clearValidationErrorForAllSettings(this.state.settings);
				this.setState({
					validationError: false
				});
				this.props.onForecastPredictItemsChanged(clearedSettings);
			}
		}

		onBlur(item, value) {
			// Ensure the value is valid before setting the state and the model
			this._checkAndSetNumberValues(item, value);
		}

		onKeyPress(item, event) {
			// If the user hit enter then update the model.
			if (event.key === 'Enter') {
				this._checkAndSetNumberValues(item, event.target.value);
			}
		}

		_getNumberReactComponent(item) {
			const valueTooltip = item.showValidationError ? stringResources.get('forecast_enter_number') + item.restrictions.minimum + stringResources.get('and') + item.restrictions.maximum : item.value;
			return <div className='forecastPredictInput' key={item.id}>
				<Tooltip
					className='predictForecastTooltip'
					title={item.description}
					orient='top'
					disabled={this.props.isDisabled}>
					<Label
						className='predictForecastInputLabel'
						type='caption'
						htmlFor={item.id}
						label={item.name}
						disabled={this.props.isDisabled}
					/>
				</Tooltip>
				<div className='predictForecastInputEntry'>
					<TextInput
						className='predictForecastOptionEntry'
						light
						title={valueTooltip}
						id={item.id}
						value={item.value}
						placeholder = {item.default}
						onChange={event => this._applyNumberValuesToState(event.currentTarget.value, item)}
						onKeyPress={event => this.onKeyPress(item, event)}
						fireOnBlur={true}
						onBlur={(e) => this.onBlur(item, e.target.value)}
						invalid={item.showValidationError}
						disabled = {this.props.isDisabled}
					/>
				</div>
				<VSpacer size={1} />
			</div>;
		}

		_updateStateClearErrorsGetNewPredictItems(val, item) {
			const newPredictValues = _.clone(this.state.settings);

			newPredictValues.forEach(predictItem => {
				if(predictItem.id === item.id){
					predictItem.showValidationError = false;
					if (_.isEmpty(val)) {
						delete predictItem.value;
					} else {
						// hack for now due to diff formats btw val and ui component used (text input for ints)
						predictItem.value = item.id === 'confidenceLevel' ? val : parseInt(val);
					}
				}
			});

			this.setState({
				validationError: false
			});
			return newPredictValues;
		}

		/**
		* Handle the number input changes
		* @param {Object} value - value being selected.
		* @param {String} id - id of the predict item being modified
		*/
		_applyNumberValuesToState(val, item){
			if(this._validateNumberInputForItem(item, val)) {
				this._updateStateClearErrorsGetNewPredictItems(val, item);
			}
		}

		/**
		* Handle the input changes
		* @param {Object} value - value being selected.
		* @param {String} id - id of the predict item being modified
		*/
		_applySelectValuesToState(val, item) {
			const newPredictValues = this._updateStateClearErrorsGetNewPredictItems(val, item);
			this.props.onForecastPredictItemsChanged(newPredictValues);
		}

		_getEnumerationReactComponent(item) {
			let values = [];
			item.restrictions.values.map(item => {
				values.push({
					value:item.value,
					label: item.name
				});
			});
			let selectedValue = item.restrictions.values.find(selected => item.value === selected.value);
			selectedValue = selectedValue ? selectedValue.name : item.restrictions.values.find(selected => item.default === selected.value).name;
			const labelId = item.id;
			return <div className='forecastPredictInput' key={item.id}>
				<Tooltip
					className='predictForecastTooltip'
					title={item.description}
					display ='inline-block'
					orient='top'
					disabled={this.props.isDisabled}>
					<Label
						className='predictForecastInputLabel'
						id={labelId}
						type='caption'
						htmlFor={item.id}
						label={item.name}
						disabled={this.props.isDisabled}
					/>
				</Tooltip>
				<div className='predictForecastInputEntry'>
					<Dropdown
						className='predictForecastOptionEntry'
						id={item.id}
						selectedItem={selectedValue}
						disabled={this.props.isDisabled}
						onChange={value => this._applySelectValuesToState(value.selectedItem.value, item)}
						items={values}
						light
						ariaLabel={item.name}
					/>
				</div>
				<VSpacer size={1} />
			</div>;
		}

		render() {
			const predictItems = this.state.settings.map(item => {
				if (item.restrictions.type === 'enumeration') {
					return this._getEnumerationReactComponent(item);
				} else {
					return this._getNumberReactComponent(item);
				}
			});
			return(
				<FlexLayout className='predictForecastOptions' wrap='wrap' justifyContent='space-between'>
					{predictItems}
				</FlexLayout>

			);
		}
	}
	return ForecastPredictComponents;
});
